import { useCallback, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Checkbox } from '@mui/material';
import useSWR from 'swr';
import * as Yup from 'yup';
import { formatPhoneNumber, formatZipcode } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { TextInputField } from '@shared/fields/TextInputField';
import { statesList } from '@shared/constants/statesList';
import { ZipCodeInputField } from '@shared/fields/ZipCodeInputField';
import { PhoneInputField } from '@shared/fields/PhoneInputField';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { TableModal } from '@shared/components/modals/TableModal';
import { SERVICES } from '@shared/constants/apiConfig';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { Wrapper } from '@styles/CommonStyles';
import { LienHolderAddress } from './interfaces';
import { requestCreateAddress, requestDeleteAddress, requestUpdateAddress } from './requests';

const lienHolderAddressSchema = Yup.object().shape({
  name: Yup.string().min(1).max(128).required(),
  address: Yup.object().shape({
    streetLine1: Yup.string().required('Address is required').max(34, 'Address 1 should be max 34 characters'),
    streetLine2: Yup.string().nullable().max(34, 'Address 2 should be max 34 characters'),
    city: Yup.string().required().max(128, 'City should be max 128 symbols'),
    state: Yup.string().required(),
    zip: Yup.string().zipCode().nullable().required('Zip is required'),
  }),
  phoneNumber: Yup.string().phone().nullable().required('Phone Number is required'),
});

export const LienHolderAddresses = () => {
  const { data: addresses = [] } = useSWR<LienHolderAddress[]>(`/${SERVICES.ASSET}/lienholder`, {revalidateOnMount: undefined});
  const [currentAddresses, setCurrentAddresses] = useState<LienHolderAddress | null>(null);
  const [isSubmitting, setSubmittingStatus] = useState(false);

  const formConfig = useMemo(
    () => [
      {
        width: '100%',
        name: 'name',
        label: 'Lien Holder Name',
        disabled: !!currentAddresses?.name,
        component: TextInputField,
      },
      {
        width: '50%',
        name: 'address.streetLine1',
        label: 'Address 1',
        component: TextInputField,
      },
      {
        width: '50%',
        name: 'address.streetLine2',
        label: 'Address 2',
        component: TextInputField,
      },
      {
        width: '40%',
        name: 'address.city',
        label: 'City',
        component: TextInputField,
      },
      {
        width: '30%',
        name: 'address.state',
        label: 'State',
        options: statesList,
        component: SelectAutocompleteField,
      },
      {
        width: '30%',
        name: 'address.zip',
        label: 'Zip',
        component: ZipCodeInputField,
      },
      {
        width: '40%',
        name: 'phoneNumber',
        label: 'Phone Number',
        component: PhoneInputField,
      },
      {
        width: '20%',
        name: 'showBanner',
        label: 'Show Banner',
        component: CheckboxField,
      },
      {
        width: '20%',
        name: 'isSecondLien',
        label: 'Second Lien',
        component: CheckboxField,
      },
    ],
    [currentAddresses]
  );

  const columns: GridColDef<LienHolderAddress>[] = useMemo(
    () =>
      [
        dataGridCol('name', {
          minWidth: 250,
          headerName: 'Lien Holder Name',
        }),
        dataGridCol('address1', {
          minWidth: 200,
          valueGetter: ({ row }) => row.address?.streetLine1 || '—',
        }),
        dataGridCol('address2', {
          minWidth: 200,
          valueGetter: ({ row }) => row.address?.streetLine2 || '—',
        }),
        dataGridCol('city', {
          minWidth: 150,
          valueGetter: ({ row }) => row.address?.city || '—',
        }),
        dataGridCol('state', {
          minWidth: 100,
          valueGetter: ({ row }) => row.address?.state || '—',
        }),
        dataGridCol('zip', {
          minWidth: 150,
          valueGetter: ({ row }) => row.address?.zip || '—',
          valueFormatter: ({ value }) => formatZipcode(value) || '—',
        }),
        dataGridCol('phoneNumber', {
          minWidth: 150,
          valueFormatter: ({ value }) => (value ? formatPhoneNumber(value) : '—'),
          headerName: 'Phone',
        }),
        dataGridCol('showBanner', {
          renderCell: ({ row }) => (
            <Checkbox
              disabled
              checked={row.showBanner}
            />
          ),
          valueGetter: ({ row }) => row.showBanner,
          minWidth: 150,
        }),
        dataGridCol('isSecondLien', {
          minWidth: 150,
          headerName: 'Second Lien',
          renderCell: ({ row }) => (
            <Checkbox
              disabled
              checked={row.isSecondLien}
            />
          ),
        }),
      ].map((column) => ({ ...column, flex: 1 })),
    []
  );

  const gridActions = {
    onEdit: (data: LienHolderAddress) => setCurrentAddresses(data),
    onDelete: (data: LienHolderAddress) => requestDeleteAddress(data.id || ''),
  };

  const handleSubmit = useCallback(
    async (data: LienHolderAddress) => {
      setSubmittingStatus(true);

      const newData = { ...data, name: data.name.trim() };

      try {
        if (data.id) {
          await requestUpdateAddress(newData);
        } else {
          await requestCreateAddress(newData);
        }

        setCurrentAddresses(null);
      } finally {
        setSubmittingStatus(false);
      }
    },
    []
  );

  const editingData = useMemo(
    () => ({
      id: currentAddresses?.id,
      name: currentAddresses?.name ?? '',
      description: currentAddresses?.description ?? '',
      numericId: currentAddresses?.numericId ?? 0,
      showBanner: currentAddresses?.showBanner ?? false,
      isSecondLien: currentAddresses?.isSecondLien ?? false,
      address: currentAddresses?.address ?? {
        city: '',
        state: '',
        county: '',
        streetLine1: '',
        streetLine2: '',
        zip: '',
      },
      acumaticaVendorId: currentAddresses?.acumaticaVendorId ?? '',
      phoneNumber: currentAddresses?.phoneNumber ?? '',
    }),
    [currentAddresses]
  );

  return (
    <Wrapper>
      <TableModal
        title="Lien Holder Address"
        isOpen={!!currentAddresses}
        editingData={editingData}
        formConfig={formConfig}
        validationSchema={lienHolderAddressSchema}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onClose={() => setCurrentAddresses(null)}
      />

      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        strict
        buttonTitle="Add New"
        name="Lien Holder Address"
        columns={columns}
        loading={!addresses}
        actions={gridActions}
        validateSchema={lienHolderAddressSchema}
        rows={addresses}
        onButtonClick={() => setCurrentAddresses({} as LienHolderAddress)}
      />
    </Wrapper>
  );
};
